import { useMutation } from '@tanstack/react-query';

import { RequestError } from '@/services/config';
import { AuditLogController_export } from '@/services/services';
import { saveBlob } from '@/utils/saveBlob';

import { isRecord } from './mappers';
import { toAuditLogQueryParams } from './queryParams';
import type { AuditLogFilters, UseExportAuditLogResult } from './types';

export const AUDIT_EXPORT_TOO_LARGE = 'AUDIT_EXPORT_TOO_LARGE';
export type AuditLogExportErrorCode = typeof AUDIT_EXPORT_TOO_LARGE;

/**
 * An export the backend refused for a reason the page can explain, such as more than
 * `limit` entries matching. Any other failure (network, gateway, validation, unreadable
 * body) is rethrown as-is and gets the page's generic message.
 */
export class AuditLogExportError extends Error {
    readonly code: AuditLogExportErrorCode;
    readonly limit: number;

    constructor(code: AuditLogExportErrorCode, limit: number) {
        super(`Audit log export refused: ${code} (limit ${limit})`);
        this.name = 'AuditLogExportError';
        this.code = code;
        this.limit = limit;
    }
}

function twoDigits(value: number): string {
    return String(value).padStart(2, '0');
}

/**
 * Named from the browser's own date: the server's is UTC, which is already tomorrow's
 * date in the evening for anyone west of it and yesterday's after midnight east of it.
 */
export function auditLogExportFilename(now: Date = new Date()): string {
    const date = `${now.getFullYear()}-${twoDigits(now.getMonth() + 1)}-${twoDigits(now.getDate())}`;
    return `audit-log-${date}.csv`;
}

function readBlobAsText(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
        reader.readAsText(blob);
    });
}

async function jsonFromBlob(blob: Blob): Promise<unknown> {
    try {
        return JSON.parse(await readBlobAsText(blob));
    } catch {
        return null;
    }
}

/**
 * The generated client rejects every HTTP error as a RequestError, and with
 * `responseType: 'blob'` even a JSON error body arrives as a Blob.
 */
async function refusalFrom(error: unknown): Promise<AuditLogExportError | null> {
    if (!RequestError.isRequestError(error) || error.status !== 400) return null;
    const data: unknown = error.response?.data;
    if (!(data instanceof Blob)) return null;

    const body = await jsonFromBlob(data);
    if (!isRecord(body) || body.code !== AUDIT_EXPORT_TOO_LARGE) return null;
    const { limit } = body;
    if (typeof limit !== 'number' || !Number.isSafeInteger(limit)) return null;
    return new AuditLogExportError(AUDIT_EXPORT_TOO_LARGE, limit);
}

export async function exportAuditLog(filters: AuditLogFilters): Promise<void> {
    let response: Awaited<ReturnType<typeof AuditLogController_export>>;
    try {
        response = await AuditLogController_export(toAuditLogQueryParams(filters), {
            responseType: 'blob',
        });
    } catch (error) {
        const refusal = await refusalFrom(error);
        throw refusal ?? error;
    }

    // The generated client types this response body as `string`, inferred from the
    // endpoint's declared content-type, but `responseType: 'blob'` above makes axios
    // hand back an actual Blob at runtime. Widening to `unknown` first (not a cast:
    // a plain assignment) is what lets `instanceof` narrow it correctly.
    const data: unknown = response.data;
    if (!(data instanceof Blob)) {
        throw new Error('Unexpected export response');
    }

    saveBlob(data, auditLogExportFilename());
}

export function useExportAuditLog(): UseExportAuditLogResult {
    return useMutation({ mutationFn: (filters: AuditLogFilters) => exportAuditLog(filters) });
}
